import { useAppDispatch, useAppSelector } from "../../../redux/store";
import React, { useEffect, useState } from "react";
import { Message, MessageSections } from "../../components/message/message";
import { getCurrencies } from "../../../redux/slices/static-data.slice";
import BarekamText from "../../components/text";
import { BalanceCardContent, BalanceContainer, BalancePageCard } from "./styled";
import 'react-credit-cards-2/dist/es/styles-compiled.css';
import { getBindings, getCurrentBalance, validateBalance } from "../../../redux/slices/balance-slice";
import BankCards from "./bank-cards/bank-cards";
import { StandardDialog } from "../../components/dialogs/standard-dialog/standard-dialog";
import TopUpDialog from "./top-up-dialog/top-up-dialog";
import paymentService from "../../../services/payment-service";
import { BalanceModel } from "../../../redux/reducers/balance-reducers";
import { toast, ToastContainer } from "react-toastify";
import { ActionBar } from "../../components/action-bar/action-bar";
import { BarekamIcon, IconVariants } from "../../components/icon/barekam-icon";
import { getMessage, initToast } from "../../shared/helper-functions";
import { useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";

export const Balance = () => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const balanceLeft = searchParams.get('balanceLeft');

    const { balance, bonus, bindings } = useAppSelector(state => state.balanceReducer);
    const amdBalanceModel = balance.find(balanceModel => balanceModel.currency.currencyId === 2);
    const bonusBalanceModel = bonus.find(balanceModel => balanceModel.currency.currencyId === 2);
    const amdAmount = amdBalanceModel ? amdBalanceModel.amount : 0;
    const amdBonus = bonusBalanceModel ? bonusBalanceModel.amount : 0;

    const [topUpOpen, setTopUpOpen] = useState<boolean>(false);
    const [initialBalance, setInitialBalance] = useState<BalanceModel[]>(balance);

    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    const dispatch = useAppDispatch();
    const params = new URLSearchParams(window.location.search);

    useEffect(() => {
        if (params.get('resposneCode') && params.get('resposneCode')?.trim() === '00' && params.get('paymentID')) {
            dispatch(validateBalance({ paymentId: params.get('paymentID') || '', paymentTypeId: 1, paymentMethod: 1, currency: 2 }));
            toast['success'](
                getMessage('balance_refilled', MessageSections.MAIN, messageCache, languageId), {
                position: 'top-right',
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }, []);

    useEffect(() => {
        if (balanceLeft) {
            setTopUpOpen(true);
        }
    }, [balanceLeft]);

    useEffect(() => {
        Promise.allSettled([
            dispatch(getCurrentBalance()),
            dispatch(getCurrencies()),
            dispatch(getBindings())
        ]).then(() => setIsReady(true))
    }, [isReady]);

    useEffect(() => {
        initialBalance.forEach(balanceForCurrency => {
            const currencyToCheck = balance.find(newBalanceForCurrency =>
                balanceForCurrency.currency.currencyId === newBalanceForCurrency.currency.currencyId);

            if (currencyToCheck && currencyToCheck.amount > balanceForCurrency.amount) {
                toast['success'](
                    getMessage('balance_refilled', MessageSections.MAIN, messageCache, languageId), {
                    position: 'top-right',
                    autoClose: 10000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        })

        setInitialBalance(balance);
    }, [balance])

    async function handleSubmitPayment(selectedCardIndex: number | null, amount: number | null) {
        if (amount) {
            if (selectedCardIndex !== null) {
                await paymentService.topUpBalanceWithBinding(bindings[selectedCardIndex].CardHolderID, amount)
                    .then(payload => {
                        if (payload && payload.data && payload.data.responseCode === '00') {
                            dispatch(validateBalance({
                                paymentId: payload.data.paymentId || '',
                                paymentTypeId: 1,
                                paymentMethod: 1,
                                currency: 2
                            }));
                        }
                        setTopUpOpen(false);
                    }).catch(e => {
                        if (e instanceof AxiosError) {
                            const responseData = e?.response && e?.response?.data || e;
                            toast['error'](
                                getMessage(responseData?.reason || 'something_wrong', MessageSections.MAIN, messageCache, languageId), {
                                position: 'top-right',
                                autoClose: 10000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                            });
                            setTopUpOpen(false);
                        }
                    });
            }
        }
    }

    function topUpBalance() {
        setTopUpOpen(true);
    }

    if (isReady) {
        return (
            <>
                <BalanceContainer>
                    <ActionBar buttonText={<>
                        <BarekamIcon variant={IconVariants.plus} size={{ width: '1rem', height: '1rem' }} />

                        <BarekamText variant={'m'} fontWeight={700}>
                            <Message messageKey={'top_up'} sectionId={MessageSections.MAIN} />
                        </BarekamText>
                    </>}
                        headerText={<Message messageKey={'balance'} sectionId={MessageSections.MAIN} />}
                        buttonFunction={topUpBalance} />

                    <BalancePageCard>
                        <BalanceCardContent>
                            <BarekamText variant={"m"} opaque>
                                <Message messageKey={'current_balance'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                            <BarekamText variant={"l"} bold>
                                {amdAmount} {' '}
                                <Message messageKey={'amd'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                        </BalanceCardContent>
                        <BalanceCardContent>
                            <BarekamText variant={"m"} opaque>
                                <Message messageKey={'bonuses'} sectionId={MessageSections.MAIN} />
                            </BarekamText>
                            <BarekamText variant={"l"} bold>
                                {amdBonus} {' '}
                                <Message messageKey={'amd'}
                                    sectionId={MessageSections.MAIN} />
                            </BarekamText>
                        </BalanceCardContent>
                    </BalancePageCard>

                    <BalancePageCard>
                        <BankCards removable={true}
                            bindings={bindings}
                            onCardSelect={() => null} />
                    </BalancePageCard>

                    <StandardDialog
                        headerLabel={<Message messageKey={'top_up_balance'} sectionId={MessageSections.MAIN} />}
                        open={topUpOpen}>
                        <TopUpDialog bindings={bindings} handleCancel={() => setTopUpOpen(false)}
                            handleSubmit={(index, amount) => handleSubmitPayment(index, amount)} balanceLeft={Number(balanceLeft)} />
                    </StandardDialog>
                </BalanceContainer>

                {!params.get('resposneCode') && <ToastContainer />}
            </>
        );
    } else {
        return <></>
    }
}
